/**
 * A marca que diz, sobre um item da ata, **quem vai ler aquilo**.
 *
 * **Este é o componente mais importante da F8, e ele é minúsculo de propósito.** A spec §2.5
 * manda tratar `ata_item.visivel_ao_cliente` como o isolamento entre organizações: um item errado
 * aqui não é um defeito de tela, é uma frase que alguém escreveu sobre o cliente achando que
 * ficava entre nós. O servidor já barra — `visibilidade.py` e a policy `ata_item_isolamento` da
 * `0013`, que faz a nota interna **não existir** para uma sessão de cliente. O que falta é do lado
 * de cá: quem escreve precisa ver, sem esforço e antes de publicar, para onde aquele texto vai.
 *
 * Quatro canais, e nenhum deles sozinho:
 *
 * · **Palavra.** "Vai ao cliente" / "Não vai ao cliente". A negação está escrita, e não implícita
 *   num rótulo neutro do tipo "interno": um rótulo neutro obriga quem lê a completar a frase de
 *   cabeça, e é aí que se lê o que se espera em vez do que está escrito.
 * · **Cor.** Verde e vermelho, pelos tokens, funcionando nos dois temas.
 * · **Símbolo.** `↗` sai, `✕` não sai — para quem não distingue as duas cores.
 * · **Atributo.** `data-destino` diz a mesma coisa em uma palavra, no elemento e também no item
 *   e no formulário que o contêm, para o teste automatizado poder cobrar a fronteira sem depender
 *   de texto de interface.
 *
 * E o `aria-label` carrega a frase inteira: um leitor de tela anunciando "não vai ao cliente" sem
 * contexto não diz de que item se está falando.
 */

import type { DestinoDoItem } from "../../nucleo/reuniao";
import { classes } from "../../nucleo/classes";

const PALAVRA: Record<DestinoDoItem, string> = {
  cliente: "Vai ao cliente",
  interno: "Não vai ao cliente",
};

const SIMBOLO: Record<DestinoDoItem, string> = {
  cliente: "↗",
  interno: "✕",
};

const FRASE_COMPLETA: Record<DestinoDoItem, string> = {
  cliente: "Este item vai para o cliente na ata publicada",
  interno: "Este item não vai para o cliente em lugar nenhum: só a GEOvendas lê",
};

export interface PropsDaMarcaDeDestino {
  destino: DestinoDoItem;
  className?: string;
  "data-testid"?: string;
}

export function MarcaDeDestino({ destino, className, ...resto }: PropsDaMarcaDeDestino) {
  return (
    <span
      className={classes("marca-de-destino", `marca-de-destino--${destino}`, className)}
      /* Um atributo que a tela não escreve por acaso, e que diz de que lado da mesa o item cai.
         Cor sozinha não serve nem para quem não a enxerga nem para o teste automatizado. */
      data-destino={destino}
      aria-label={FRASE_COMPLETA[destino]}
      {...resto}
    >
      <span className="marca-de-destino__simbolo" aria-hidden="true">
        {SIMBOLO[destino]}
      </span>
      {PALAVRA[destino]}
    </span>
  );
}

/**
 * O selo do tipo mais caro de errar. Existe **só** em nota interna.
 *
 * Ele é redundante com a marca de destino ao lado, e a redundância é o ponto: nota interna é o
 * único tipo que não pode atravessar por decisão nenhuma — o `CHECK`
 * `ck_ata_item_nota_interna_nunca_visivel` torna a linha impossível no banco — e quem procura
 * "onde está a nota interna nesta ata" tem de achá-la sem ler item por item.
 */
export function MarcaDeNotaInterna() {
  return (
    <span
      className="marca-de-destino marca-de-destino--interno"
      data-testid="marca-de-nota-interna"
      aria-label="Nota interna: não sai para o cliente por caminho nenhum"
    >
      <span className="marca-de-destino__simbolo" aria-hidden="true">
        ⚑
      </span>
      Nota interna
    </span>
  );
}

/**
 * A consequência por extenso, dentro do item — a repetição que é o ponto.
 *
 * Ela lista os caminhos por onde o item **não** passa, um a um, porque a pergunta que aparece na
 * cabeça de quem escreve não é "está visível?" e sim "e no resumo? e no pacote do Jira?". A
 * resposta é a mesma para todos, e vem do mesmo lugar: a policy do banco.
 */
export function FraseDoDestino({ destino, tipo }: { destino: DestinoDoItem; tipo: string }) {
  if (destino === "cliente") {
    return (
      <p className="item-da-ata__meta" data-testid="consequencia-do-item">
        Depois de publicada, esta linha aparece na área do cliente com este texto.
      </p>
    );
  }

  return (
    <p className="item-da-ata__consequencia" data-testid="consequencia-do-item">
      <b>O cliente não lê isto.</b>{" "}
      {tipo === "nota_interna"
        ? "Não aparece para ele na ata, no resumo nem em listagens. Fica registrado só nos comentários internos do GeoTask."
        : "Enquanto estiver assim, esta linha fica do lado de dentro: não aparece na ata dele, no resumo, nem no resumo de Kick Off."}
    </p>
  );
}
